import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaBarcode, FaBoxOpen, FaClipboardList, FaCubes, FaMoneyBill, FaRulerCombined, FaTag } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { useOrdensCompra } from "../../hooks/useOrdensCompra";
import { PropostaService } from "../../services/fornecedor/proposta.service";
import Money from "../../utils/money";
import { columnDescriptions } from "./columnDescriptions";
import { ListaOrdemCompra } from "./lista/ListaOrdemCompra";
import Style from "./index.module.css";

const getDefaultDates = () => {
  const hoje = new Date();
  const dataFim = hoje.toISOString().split("T")[0];
  const dataIni = new Date(hoje);
  dataIni.setFullYear(dataIni.getFullYear() - 1);

  return {
    dataInicio: dataIni.toISOString().split("T")[0],
    dataFim,
  };
};

const isEmptyDate = (value) => !value || value.startsWith("1899-12-30");

const formatDate = (value) => {
  if (isEmptyDate(value)) return "-";

  return new Date(value).toLocaleDateString("pt-BR", {
    timeZone: "UTC",
  });
};

const ORDER_COLUMNS = [
  "ordcom",
  "codope",
  "nomope",
  "valliq",
  "datped",
  "datenc",
  "datcan",
  "desfre",
  "qtdItens",
  "valtot",
  "medlar",
  "medalt",
  "status",
];

export const OrdemCompra = () => {
  const { user } = useAuth();
  const storageKey = "ordensCompraFilters";
  const defaultDates = getDefaultDates();

  if (!localStorage.getItem(`${storageKey}_dataInicio`)) {
    localStorage.setItem(`${storageKey}_dataInicio`, defaultDates.dataInicio);
  }

  if (!localStorage.getItem(`${storageKey}_dataFim`)) {
    localStorage.setItem(`${storageKey}_dataFim`, defaultDates.dataFim);
  }

  const [dataInicio, setDataInicio] = useState(localStorage.getItem(`${storageKey}_dataInicio`));
  const [dataFim, setDataFim] = useState(localStorage.getItem(`${storageKey}_dataFim`));
  const [appliedDataInicio, setAppliedDataInicio] = useState(dataInicio);
  const [appliedDataFim, setAppliedDataFim] = useState(dataFim);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [columnFilters, setColumnFilters] = useState([]);

  const result = useOrdensCompra(
    user,
    user?.codope,
    appliedDataInicio,
    appliedDataFim,
    true,
    page,
    limit,
    user?.tipusu === "FORNECEDOR",
    columnFilters
  );

  const propostasQuery = useQuery({
    queryKey: ["ordens-compra-propostas", user?.codope, appliedDataInicio, appliedDataFim],
    queryFn: async () => {
      const response = await PropostaService.getPropostasAgrupadoByCodCli(
        user.token,
        user.codope,
        appliedDataInicio,
        appliedDataFim,
        false
      );

      return Array.isArray(response) ? response : response?.docs || [];
    },
    enabled: user?.tipusu === "FORNECEDOR" && !!user?.token && !!user?.codope,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });

  const ordensComProposta = useMemo(() => {
    const propostas = propostasQuery.data || [];
    return new Set(propostas.map((proposta) => String(proposta.ordcom)));
  }, [propostasQuery.data]);

  const ordensApi = useMemo(() => {
    if (Array.isArray(result.data)) return result.data;
    return result.data?.docs || [];
  }, [result.data]);

  const ordens = useMemo(() => {
    return ordensApi.map((ordem) => ({
      ...ordem,
      valliq: Money.formatarValor(ordem.valliq),
      datped: formatDate(ordem.datped),
      datenc: formatDate(ordem.datenc),
      datcan: formatDate(ordem.datcan),
      desfre: ordem.desfre || "-",
      qtdItens: ordem.itens?.length || 0,
      temProposta: ordensComProposta.has(String(ordem.ordcom)),
    }));
  }, [ordensApi, ordensComProposta]);

  const columns = useMemo(() => {
    return ORDER_COLUMNS.map((key) => ({
      accessorKey: key,
      header: columnDescriptions[key] || key,
    }));
  }, []);

  const total = result.data?.total ?? ordens.length;

  const handleFiltroData = () => {
    localStorage.setItem(`${storageKey}_dataInicio`, dataInicio);
    localStorage.setItem(`${storageKey}_dataFim`, dataFim);
    setPage(1);
    setAppliedDataInicio(dataInicio);
    setAppliedDataFim(dataFim);
  };

  useEffect(() => {
    setColumnFilters([]);
  }, []);

  const handleFiltering = (filtros = []) => {
    setPage(1);
    setColumnFilters(filtros);
  };

  const renderItens = (ordem) => {
    const itens = ordem.itens || [];

    if (!itens.length) {
      return (
        <div className={Style.itensContainer}>
          <span className={Style.emptyItens}>Nenhum item encontrado para esta ordem.</span>
        </div>
      );
    }

    return (
      <div className={Style.itensContainer}>
        <div className={Style.itensHeader}>
          <div className={Style.itensTitle}>
            <div>
              <strong>Itens da ordem</strong>
            </div>
          </div>
        </div>

        <table className={Style.itensTable}>
          <thead>
            <tr>
              <th><span className={Style.itemHeaderCell}><FaBarcode /> Produto</span></th>
              <th><span className={Style.itemHeaderCell}><FaBoxOpen /> Nome</span></th>
              <th><span className={Style.itemHeaderCell}><FaCubes /> Quantidade</span></th>
              <th><span className={Style.itemHeaderCell}><FaMoneyBill /> Valor</span></th>
              <th><span className={Style.itemHeaderCell}><FaRulerCombined /> Unidade</span></th>
              <th><span className={Style.itemHeaderCell}><FaRulerCombined /> Largura</span></th>
              <th><span className={Style.itemHeaderCell}><FaRulerCombined /> Altura</span></th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, index) => (
              <tr key={`${ordem.ordcom}-${item.codpro}-${index}`}>
                <td>{item.codpro || "-"}</td>
                <td>{item.nompro || "-"}</td>
                <td>{item.qtdade || "0"}</td>
                <td>{item.valtot || "0"}</td>
                <td>{item.unidade || "-"}</td>
                <td>{item.medlar || "-"}</td>
                <td>{item.medalt || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <ListaOrdemCompra
      columns={columns}
      data={ordens}
      page={page}
      setPage={setPage}
      limit={limit}
      setLimit={setLimit}
      total={total}
      loading={result.isLoading || result.isFetching || propostasQuery.isFetching}
      storageKey={storageKey}
      setDataInicio={setDataInicio}
      dataInicio={dataInicio}
      setDataFim={setDataFim}
      dataFim={dataFim}
      handleFiltroData={handleFiltroData}
      handleFiltering={handleFiltering}
      renderExpandedRow={renderItens}
      getRowId={(ordem) => ordem.ordcom}
      getRowStyle={(ordem) => ordem.temProposta ? { backgroundColor: "#eaf6ff" } : {}}
      legend={[
        { color: "#eaf6ff", label: "Com proposta/negociação" }
      ]}
    />
  );
};
